/** 経路の上の位置と区間の計算。経路ごとの累積長はキャッシュする */
import type { Vec3 } from '../../core/vec';
import type { EdgeData, EdgeSegment } from '../../core/world';

interface EdgeGeom {
  /** segments の累積の水平距離（先頭 0、末尾 lengthM） */
  segCum: Float64Array;
  /** polyline の累積の水平距離 */
  polyCum: Float64Array;
}

const cache = new WeakMap<EdgeData, EdgeGeom>();

function geom(e: EdgeData): EdgeGeom {
  let g = cache.get(e);
  if (g) return g;
  const segCum = new Float64Array(e.segments.length + 1);
  e.segments.forEach((s, i) => (segCum[i + 1] = (segCum[i] as number) + s.lengthM));
  const n = e.polyline.length / 3;
  const polyCum = new Float64Array(Math.max(n, 1));
  for (let i = 1; i < n; i++) {
    const dx = (e.polyline[i * 3] as number) - (e.polyline[i * 3 - 3] as number);
    const dz = (e.polyline[i * 3 + 2] as number) - (e.polyline[i * 3 - 1] as number);
    polyCum[i] = (polyCum[i - 1] as number) + Math.hypot(dx, dz);
  }
  g = { segCum, polyCum };
  cache.set(e, g);
  return g;
}

/** a からの距離 d にある区間 */
export function segmentAt(e: EdgeData, d: number): EdgeSegment {
  const { segCum } = geom(e);
  let lo = 0;
  let hi = e.segments.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((segCum[mid] as number) <= d) lo = mid;
    else hi = mid - 1;
  }
  return e.segments[lo] as EdgeSegment;
}

/** a からの距離 d の位置（polyline を長さの比率で対応づける） */
export function pointAlongEdge(e: EdgeData, d: number, out: Vec3): Vec3 {
  const { polyCum } = geom(e);
  const n = polyCum.length;
  const total = polyCum[n - 1] as number;
  const target = e.lengthM > 0 ? (Math.min(Math.max(d, 0), e.lengthM) / e.lengthM) * total : 0;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((polyCum[mid] as number) <= target) lo = mid;
    else hi = mid - 1;
  }
  const i = Math.min(lo, n - 2);
  const p = e.polyline;
  if (n < 2) {
    out.x = p[0] ?? 0;
    out.y = p[1] ?? 0;
    out.z = p[2] ?? 0;
    return out;
  }
  const c0 = polyCum[i] as number;
  const c1 = polyCum[i + 1] as number;
  const t = c1 > c0 ? (target - c0) / (c1 - c0) : 0;
  out.x = (p[i * 3] as number) + ((p[i * 3 + 3] as number) - (p[i * 3] as number)) * t;
  out.y = (p[i * 3 + 1] as number) + ((p[i * 3 + 4] as number) - (p[i * 3 + 1] as number)) * t;
  out.z = (p[i * 3 + 2] as number) + ((p[i * 3 + 5] as number) - (p[i * 3 + 2] as number)) * t;
  return out;
}
